import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import ReuploadApiResponseDialog from '../../../components/ReuploadDocuments/ReuploadApiResponseDialog';
import ReuploadDocumentsPageLayout from '../../../components/ReuploadDocuments/ReuploadDocumentsPageLayout';
import {
    ReuploadDocumentSlot,
    ReuploadDocumentsGrid,
    buildReuploadDocumentRemarks,
    buildReuploadStatusBarFromSlots,
    createReuploadUploadHandler,
    isDocFlagApproved,
    isDocFlagRejected,
    isReuploadSubmitDisabled,
    preserveReuploadDocumentValue,
    pickReuploadDocumentUrl,
    REUPLOAD_DOCUMENT_URL_KEYS,
    shouldShowReuploadDocumentSlot,
} from '../../../components/ReuploadDocuments/reuploadDocumentUi';
import { cfpBaseUrl } from '../../../network/urls';
import { portalName } from '../../../utils/PortalName';

const ChangeofAttributesupdatedocuments = () => {
    const initialValues = {
        iv_applicant_cnic_front: null,
        iv_applicant_cnic_back: null,
        iv_owenership_document: null,
        iv_electricityBill_document: null,
        iv_fbr_document: null,
        iv_any_other_document: null,
    };

    const [apiResponseDialog, setApiResponseDialog] = useState({
        open: false,
        type: 'success',
        message: '',
        documentRemarks: [],
    });
    const [submitCompleted, setSubmitCompleted] = useState(false);
    const [applicantCNICFrontThumbnail, setApplicantCnicFrontThumbnail] = useState('');
    const [applicantCNICBackThumbnail, setApplicantCnicBackThumbnail] = useState('');
    const [owenershipDocumentThumbnail, setOwenershipDocumentThumbnail] = useState('');
    const [electricityBillDocumentThumbnail, setElectricityBillDocumentThumbnail] = useState('');
    const [fbrDocumentThumbnail, setFbrDocumentThumbnail] = useState('');
    const [anyOtherDocumentThumbnail, setAnyOtherDocumentThumbnail] = useState('');
    const [applicantCNICFrontError, setApplicantCnicFrontError] = useState('');
    const [applicantCNICBackError, setApplicantCnicBackError] = useState('');
    const [owenershipDocumentError, setOwenershipDocumentError] = useState('');
    const [electricityBillDocumentError, setElectricityBillDocumentError] = useState('');
    const [fbrDocumentError, setFbrDocumentError] = useState('');
    const [anyOtherDocumentError, setAnyOtherDocumentError] = useState('');
    const [viewBackdrop, setViewBackdrop] = useState(false);
    const [error, setError] = useState(false);
    const [cnicFrontReUploaded, setCnicFrontReUploaded] = useState(false);
    const [cnicBackReUploaded, setCnicBackReUploaded] = useState(false);
    const [ownershipReUploaded, setOwnershipReUploaded] = useState(false);
    const [electricityBillReUploaded, setElectricityBillReUploaded] = useState(false);
    const [fbrReUploaded, setFbrReUploaded] = useState(false);
    const [anyOtherReUploaded, setAnyOtherReUploaded] = useState(false);

    const { state } = useLocation();
    const { dialogRowData } = state || {};
    const { token } = useSelector((authState) => authState.auth);
    const navigate = useNavigate();
    const reuploadSources = [dialogRowData];

    useEffect(() => {
        setApplicantCnicFrontThumbnail(pickReuploadDocumentUrl(dialogRowData, REUPLOAD_DOCUMENT_URL_KEYS.cnicFront));
        setApplicantCnicBackThumbnail(pickReuploadDocumentUrl(dialogRowData, REUPLOAD_DOCUMENT_URL_KEYS.cnicBack));
        setOwenershipDocumentThumbnail(pickReuploadDocumentUrl(dialogRowData, REUPLOAD_DOCUMENT_URL_KEYS.ownership));
        setElectricityBillDocumentThumbnail(pickReuploadDocumentUrl(dialogRowData, REUPLOAD_DOCUMENT_URL_KEYS.electricityBill));
        setFbrDocumentThumbnail(pickReuploadDocumentUrl(dialogRowData, REUPLOAD_DOCUMENT_URL_KEYS.fbr));
        setAnyOtherDocumentThumbnail(pickReuploadDocumentUrl(dialogRowData, REUPLOAD_DOCUMENT_URL_KEYS.anyOther));
    }, [dialogRowData]);

    const isCnicFrontRejected = isDocFlagRejected(dialogRowData?.applicantCNICFrontFlag);
    const isCnicBackRejected = isDocFlagRejected(dialogRowData?.applicantCNICBackFlag);
    const isOwnershipRejected = isDocFlagRejected(dialogRowData?.ownershipImageFlag);
    const isElectricityBillRejected = isDocFlagRejected(dialogRowData?.electricityBillFlag);
    const isFbrRejected = isDocFlagRejected(dialogRowData?.fbrDocumentFlag);
    const isAnyOtherRejected = isDocFlagRejected(dialogRowData?.anyOtherDocumentFlag);

    const reuploadSlots = useMemo(() => ([
        { isRejected: isCnicFrontRejected, isReUploaded: cnicFrontReUploaded },
        { isRejected: isCnicBackRejected, isReUploaded: cnicBackReUploaded },
        { isRejected: isOwnershipRejected, isReUploaded: ownershipReUploaded },
        { isRejected: isElectricityBillRejected, isReUploaded: electricityBillReUploaded },
        ...(shouldShowReuploadDocumentSlot({
            documentValue: pickReuploadDocumentUrl(dialogRowData, REUPLOAD_DOCUMENT_URL_KEYS.fbr),
            flag: dialogRowData?.fbrDocumentFlag,
        }) ? [{ isRejected: isFbrRejected, isReUploaded: fbrReUploaded }] : []),
        ...(shouldShowReuploadDocumentSlot({
            documentValue: pickReuploadDocumentUrl(dialogRowData, REUPLOAD_DOCUMENT_URL_KEYS.anyOther),
            flag: dialogRowData?.anyOtherDocumentFlag,
        }) ? [{ isRejected: isAnyOtherRejected, isReUploaded: anyOtherReUploaded }] : []),
    ]), [
        isCnicFrontRejected, isCnicBackRejected, isOwnershipRejected, isElectricityBillRejected,
        isFbrRejected, isAnyOtherRejected,
        cnicFrontReUploaded, cnicBackReUploaded, ownershipReUploaded, electricityBillReUploaded,
        fbrReUploaded, anyOtherReUploaded, dialogRowData,
    ]);

    const reuploadStatusBar = useMemo(
        () => buildReuploadStatusBarFromSlots(reuploadSlots),
        [reuploadSlots],
    );

    const disableSubmit = isReuploadSubmitDisabled(reuploadSlots, submitCompleted);

    const { handleChange, handleSubmit } = useFormik({
            initialValues,
        validationSchema: Yup.object({}),
        onSubmit: async () => {
            setViewBackdrop(true);

                const myHeaders = new Headers();
            myHeaders.append('Content-Type', 'application/json');
            myHeaders.append('Authorization', token);

                const raw = JSON.stringify({
                applicationId: dialogRowData.applicationId,
                submittedThrough: portalName,
                applicationProgressId: 1,
                objectionStatus: 0,
                remarks: 'Documents Re-uploaded.',
                remarksDetails: 'Documents Re-uploaded.',
                applicantCNICFront: preserveReuploadDocumentValue(applicantCNICFrontThumbnail, dialogRowData?.applicantCNICFront),
                applicantCNICBack: preserveReuploadDocumentValue(applicantCNICBackThumbnail, dialogRowData?.applicantCNICBack),
                ownershipImage: preserveReuploadDocumentValue(
                    owenershipDocumentThumbnail,
                    pickReuploadDocumentUrl(dialogRowData, REUPLOAD_DOCUMENT_URL_KEYS.ownership),
                ),
                electricityBill: preserveReuploadDocumentValue(
                    electricityBillDocumentThumbnail,
                    pickReuploadDocumentUrl(dialogRowData, REUPLOAD_DOCUMENT_URL_KEYS.electricityBill),
                ),
                fbrDocument: preserveReuploadDocumentValue(
                    fbrDocumentThumbnail,
                    pickReuploadDocumentUrl(dialogRowData, REUPLOAD_DOCUMENT_URL_KEYS.fbr),
                ),
                anyOtherDocument: preserveReuploadDocumentValue(
                    anyOtherDocumentThumbnail,
                    pickReuploadDocumentUrl(dialogRowData, REUPLOAD_DOCUMENT_URL_KEYS.anyOther),
                ),
                applicantCNICFrontFlag: 2,
                applicantCNICBackFlag: 2,
                ownershipImageFlag: 2,
                electricityBillFlag: 2,
                fbrDocumentFlag: preserveReuploadDocumentValue(
                    fbrDocumentThumbnail,
                    pickReuploadDocumentUrl(dialogRowData, REUPLOAD_DOCUMENT_URL_KEYS.fbr),
                ) ? 2 : null,
                anyOtherDocumentFlag: preserveReuploadDocumentValue(
                    anyOtherDocumentThumbnail,
                    pickReuploadDocumentUrl(dialogRowData, REUPLOAD_DOCUMENT_URL_KEYS.anyOther),
                ) ? 2 : null,
            });

            try {
                const response = await fetch(`${cfpBaseUrl}changeOfAttributes/reuploadDocuments`, {
                    method: 'POST',
                    headers: myHeaders,
                    body: raw,
                    redirect: 'follow',
                });
                const result = await response.json();

                if (result.status === '200' || result.status === 200) {
                    setSubmitCompleted(true);
                    setApiResponseDialog({
                        open: true,
                        type: 'success',
                        message: result.message || 'Documents re-uploaded successfully.',
                        documentRemarks: buildReuploadDocumentRemarks({}, dialogRowData),
                    });
                } else {
                    setApiResponseDialog({
                        open: true,
                        type: 'error',
                        message: result.message || 'Something went wrong.',
                        documentRemarks: [],
                    });
                }
            } catch (submitError) {
                setApiResponseDialog({
                    open: true,
                    type: 'error',
                    message: 'Failed to re-upload documents.',
                    documentRemarks: [],
                });
            } finally {
                setViewBackdrop(false);
            }
        },
    });

    const makeUploadHandler = (setThumbnail, setUploadError, setReUploaded, filePrefix) =>
        createReuploadUploadHandler({
            handleChange,
            setUploadError,
            setThumbnail,
            setReUploaded,
            filePrefix,
        });

    const handleApiResponseDialogClose = () => {
        const wasSuccess = apiResponseDialog.type === 'success';
        setApiResponseDialog((prev) => ({ ...prev, open: false }));
        if (wasSuccess) {
            navigate('/change-of-attributes-table', { state: { refreshCoaList: true } });
        }
    };

    return (
        <>
            <ReuploadDocumentsPageLayout
                pageTitle="Change of Attributes - Re-Upload Documents"
                statusBar={reuploadStatusBar}
                dialogRowData={dialogRowData}
                onSubmit={handleSubmit}
                onCancel={() => navigate('/change-of-attributes-table')}
                submitDisabled={disableSubmit}
                viewBackdrop={viewBackdrop}
                error={error}
                onErrorClose={() => setError('')}
            >
                <ReuploadDocumentsGrid>
                    <ReuploadDocumentSlot
                        label="CNIC Front"
                        thumbnail={applicantCNICFrontThumbnail}
                        documentSources={reuploadSources}
                        documentUrlKeys={REUPLOAD_DOCUMENT_URL_KEYS.cnicFront}
                        isApproved={isDocFlagApproved(dialogRowData?.applicantCNICFrontFlag)}
                        isRejected={isCnicFrontRejected}
                        isReUploaded={cnicFrontReUploaded}
                        inputId="iv_applicant_cnic_front"
                        inputName="iv_applicant_cnic_front"
                        onUpload={makeUploadHandler(setApplicantCnicFrontThumbnail, setApplicantCnicFrontError, setCnicFrontReUploaded, 'applicantCNICFront')}
                        uploadError={applicantCNICFrontError}
                    />
                    <ReuploadDocumentSlot
                        label="CNIC Back"
                        thumbnail={applicantCNICBackThumbnail}
                        documentSources={reuploadSources}
                        documentUrlKeys={REUPLOAD_DOCUMENT_URL_KEYS.cnicBack}
                        isApproved={isDocFlagApproved(dialogRowData?.applicantCNICBackFlag)}
                        isRejected={isCnicBackRejected}
                        isReUploaded={cnicBackReUploaded}
                        inputId="iv_applicant_cnic_back"
                        inputName="iv_applicant_cnic_back"
                        onUpload={makeUploadHandler(setApplicantCnicBackThumbnail, setApplicantCnicBackError, setCnicBackReUploaded, 'applicantCNICBack')}
                        uploadError={applicantCNICBackError}
                    />
                    <ReuploadDocumentSlot
                        label="Ownership Document"
                        thumbnail={owenershipDocumentThumbnail}
                        documentSources={reuploadSources}
                        documentUrlKeys={REUPLOAD_DOCUMENT_URL_KEYS.ownership}
                        isApproved={isDocFlagApproved(dialogRowData?.ownershipImageFlag)}
                        isRejected={isOwnershipRejected}
                        isReUploaded={ownershipReUploaded}
                        inputId="iv_owenership_document"
                        inputName="iv_owenership_document"
                        onUpload={makeUploadHandler(setOwenershipDocumentThumbnail, setOwenershipDocumentError, setOwnershipReUploaded, 'ownership')}
                        uploadError={owenershipDocumentError}
                    />
                    <ReuploadDocumentSlot
                        label="Electricity Bill"
                        thumbnail={electricityBillDocumentThumbnail}
                        documentSources={reuploadSources}
                        documentUrlKeys={REUPLOAD_DOCUMENT_URL_KEYS.electricityBill}
                        isApproved={isDocFlagApproved(dialogRowData?.electricityBillFlag)}
                        isRejected={isElectricityBillRejected}
                        isReUploaded={electricityBillReUploaded}
                        inputId="iv_electricityBill_document"
                        inputName="iv_electricityBill_document"
                        onUpload={makeUploadHandler(setElectricityBillDocumentThumbnail, setElectricityBillDocumentError, setElectricityBillReUploaded, 'electricityBill')}
                        uploadError={electricityBillDocumentError}
                    />
                    {shouldShowReuploadDocumentSlot({
                        documentValue: pickReuploadDocumentUrl(dialogRowData, REUPLOAD_DOCUMENT_URL_KEYS.fbr),
                        flag: dialogRowData?.fbrDocumentFlag,
                    }) ? (
                        <ReuploadDocumentSlot
                            label="FBR Document"
                            thumbnail={fbrDocumentThumbnail}
                            documentSources={reuploadSources}
                            documentUrlKeys={REUPLOAD_DOCUMENT_URL_KEYS.fbr}
                            isApproved={isDocFlagApproved(dialogRowData?.fbrDocumentFlag)}
                            isRejected={isFbrRejected}
                            isReUploaded={fbrReUploaded}
                            inputId="iv_fbr_document"
                            inputName="iv_fbr_document"
                            onUpload={makeUploadHandler(setFbrDocumentThumbnail, setFbrDocumentError, setFbrReUploaded, 'fbr')}
                            uploadError={fbrDocumentError}
                        />
                    ) : null}
                    {shouldShowReuploadDocumentSlot({
                        documentValue: pickReuploadDocumentUrl(dialogRowData, REUPLOAD_DOCUMENT_URL_KEYS.anyOther),
                        flag: dialogRowData?.anyOtherDocumentFlag,
                    }) ? (
                        <ReuploadDocumentSlot
                            label="Any Other Document"
                            thumbnail={anyOtherDocumentThumbnail}
                            documentSources={reuploadSources}
                            documentUrlKeys={REUPLOAD_DOCUMENT_URL_KEYS.anyOther}
                            isApproved={isDocFlagApproved(dialogRowData?.anyOtherDocumentFlag)}
                            isRejected={isAnyOtherRejected}
                            isReUploaded={anyOtherReUploaded}
                            inputId="iv_any_other_document"
                            inputName="iv_any_other_document"
                            onUpload={makeUploadHandler(setAnyOtherDocumentThumbnail, setAnyOtherDocumentError, setAnyOtherReUploaded, 'anyOther')}
                            uploadError={anyOtherDocumentError}
                        />
                    ) : null}
                </ReuploadDocumentsGrid>
            </ReuploadDocumentsPageLayout>

            <ReuploadApiResponseDialog
                open={apiResponseDialog.open}
                type={apiResponseDialog.type}
                onClose={handleApiResponseDialogClose}
                message={apiResponseDialog.message}
                applicationId={dialogRowData?.applicationId}
                documentRemarks={apiResponseDialog.documentRemarks}
                errorTitle="Request Failed"
            />
        </>
    );
};

export default ChangeofAttributesupdatedocuments;
